import { PageHeader } from '../../components/PageHeader';
import Typography from 'antd/lib/typography';
import Button from 'antd/lib/button';
import Table from 'antd/lib/table';
import { ColumnsType } from 'antd/lib/table/interface';
import PlusCircleOutlined from '@ant-design/icons/PlusCircleOutlined';
import UploadOutlined from '@ant-design/icons/UploadOutlined';
import DownloadOutlined from '@ant-design/icons/DownloadOutlined';
import Badge from 'antd/lib/badge';
import { WithEmptyDataTable } from '../../components/table/withEmptyDataTable';
import { DEFAULT_PAGINATION, PAGE_ACTION } from '../../domain/commonConst';
import { exportNyisoResourcesAsXlsx } from '../../domain/nyiso';
import { INyisoResource, NYISOResourceStatus } from '../../domain/nyiso/interface';
import EditOutlined from '@ant-design/icons/EditOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import { ActionList } from '../../components/table/actionList/actionList';
import { ExclamationCircleOutlined, WarningTwoTone } from '@ant-design/icons';
import App from 'antd/lib/app';
import {
    useNyisoResourceDeleteMutation,
    useNyisoMultipleResourcesToggleMutation,
    useNyisoResourcesListQuery,
    useNyisoSeasonQuery,
    useNyisoResourceReprocessMutation,
} from '../../domain/nyiso/queries';
import { ResourceStatus } from './components/ResourceStatus';
import { ResourceEnableStateSwitch } from './components/ResourceEnableStateSwitch';
import { useState, useMemo } from 'react';
import { ResourceFormModal } from './components/ResourceFormModal';
import { usePageLocation } from '../usePageState';
import { useLocation } from 'react-router-dom';
import { toUsefulParams } from '../toUsefulParams';
import { useDocumentTitle } from '../../components/useDocumentTitle';
import './nyiso.css';
import { WARNING_COLOR, SECONDARY_COLOR } from '../../theme';
import Popover from 'antd/lib/popover';
import { getCurrentSeasonInfo } from '../../domain/nyiso/getCurrentSeasonInfo';
import { PeakHoursUploadModal } from './components/PeakHoursUploadModal';
import { DEFAULT_DATE_FORMAT, getComputerFormattedTime } from '../../domain/date/date';
import { validatePagination } from '../../domain/validatePagination';
import { IPaginationMeta } from '../../domain/IPagination';
import SyncOutlined from '@ant-design/icons/lib/icons/SyncOutlined';
import Tooltip from 'antd/lib/tooltip';
import QuestionCircleOutlined from '@ant-design/icons/QuestionCircleOutlined';
import { getTablePaginationParams } from 'src/components/table/getTablePaginationParams';

export const NyisoResourcesList = () => {
    const { modal, notification } = App.useApp();
    useDocumentTitle('NYISO');

    const { mutateAsync: deleteResource, isLoading: isResourceDeleteLoading } = useNyisoResourceDeleteMutation();
    const { mutate: toggleAllResources, isLoading: isResourcesToggleLoading } = useNyisoMultipleResourcesToggleMutation();
    const { mutate: setResourceToReprocess, isLoading: isResourceToReprocessLoading } = useNyisoResourceReprocessMutation();
    const [resource, setResource] = useState<INyisoResource | Partial<INyisoResource> | null>(null);
    const [pageAction, setPageAction] = useState<PAGE_ACTION | ''>();
    const [exportLoading, setExportLoading] = useState<boolean>(false);

    const location = useLocation();
    const { setPageQuery, queryToState } = usePageLocation();
    const pageState: any = queryToState(location.search);

    const listQuery = {
        pagination: pageState?.pagination ?? DEFAULT_PAGINATION,
    };

    const {
        data: resources,
        isLoading,
        isError,
        error,
        refetch: refetchResources,
    } = useNyisoResourcesListQuery(listQuery, {
        keepPreviousData: true,
        refetchInterval: 60000,
    });
    const meta = resources?.meta || ({ total: 0 } as IPaginationMeta);

    if (isError) {
        notification.error({
            key: 'nyiso-resources-fetch-error',
            message: error?.message || 'Cannot fetch NYISO resources!',
        });
    }

    const currentSeasonInfo = useMemo(() => getCurrentSeasonInfo(), []);

    const { data: seasonData, isLoading: isSeasonLoading, isError: isSeasonError, error: seasonError } = useNyisoSeasonQuery(currentSeasonInfo.id);

    const isSeasonalDataMissing = !seasonData && !isSeasonError && !isSeasonLoading;
    const isAllowedToDownloadResources = !isSeasonalDataMissing && resources && resources?.meta?.total > 0;

    if (isSeasonError) {
        notification.error({
            key: 'nyiso-season-fetch-error',
            message: seasonError?.message || 'Cannot fetch NYISO season!',
        });
    }

    async function onTableChange(pagination: any, filter: any, sorter: any) {
        const preparedParams = toUsefulParams({ pagination, filter, sorter });
        setPageQuery({ ...pageState, ...preparedParams });
    }

    const onResourceFormModalOpen = (record?: any) => {
        setPageAction(PAGE_ACTION.NEW_EDIT);
        setResource(
            record || {
                resource_name: undefined,
                resource_id: undefined,
            }
        );
    };

    const onResourceModalClose = () => {
        setPageAction('');
        setResource(null);
    };

    const onSeasonModalClose = () => {
        setPageAction('');
    };

    const onSeasonModalOpen = () => {
        setPageAction(PAGE_ACTION.UPLOAD_FILE);
    };

    const onEnrollmentFileRequest = async () => {
        try {
            setExportLoading(true);
            await exportNyisoResourcesAsXlsx(currentSeasonInfo.id);
        } catch (err: any) {
            notification.error({
                key: 'nyiso-resources-file-error',
                message: err?.message || 'Cannot download NYISO resources file!',
            });
        } finally {
            setExportLoading(false);
        }
    };

    const deleteResourceWithConfirm = (record: INyisoResource) => {
        if (isResourceDeleteLoading) return;

        modal.confirm({
            icon: null,
            title: 'Confirm Delete',
            closeIcon: true,
            closable: true,
            width: 500,
            styles: {
                body: {
                    padding: '24px 24px 16px 24px',
                },
            },
            content: (
                <div>
                    <ExclamationCircleOutlined style={{ fontSize: 14, color: WARNING_COLOR, marginRight: 8 }} />
                    <Typography.Text>Deleting NYISO resource {record.resource_name || ''}. Are you sure?</Typography.Text>
                </div>
            ),
            onOk: async () => {
                await deleteResource(record.id);

                const updatedTotal = meta.total - 1;
                const validPagination = validatePagination(pageState.pagination, updatedTotal);

                setPageQuery({ ...pageState, pagination: validPagination });

                await refetchResources();
            },
            onCancel() {
                console.log('Resource deletion canceled');
            },
        });
    };

    const onResourceReprocess = (resource: INyisoResource) => {
        setResourceToReprocess({ resourceId: resource.id, seasonId: currentSeasonInfo.id });
    };

    const canReprocessRecord = (resource: INyisoResource): boolean => {
        if (!resource.is_enabled || isSeasonalDataMissing) return false;

        const seasonalData = resource.seasonal_energy_data ? resource.seasonal_energy_data[currentSeasonInfo.id] : null;

        if (!seasonalData) return false;

        return seasonalData?.status !== NYISOResourceStatus.PROCESSING;
    };

    const renderActions = (resource: INyisoResource) => {
        const canReprocess = canReprocessRecord(resource);
        return [
            ...(canReprocess
                ? [
                      {
                          title: 'Reprocess seasonal ACL data',
                          disabled: true,
                          onClick: (record: INyisoResource) => () => onResourceReprocess(record),
                          icon: <SyncOutlined />,
                      },
                  ]
                : []),
            {
                title: 'Edit',
                onClick: (record: INyisoResource) => () => onResourceFormModalOpen(record),
                icon: <EditOutlined />,
            },
            {
                title: 'Delete',
                onClick: (record: INyisoResource) => () => deleteResourceWithConfirm(record),
                icon: <DeleteOutlined />,
            },
        ];
    };

    const columns: ColumnsType<INyisoResource> = [
        {
            title: 'Resource Name',
            fixed: 'left',
            width: 250,
            dataIndex: 'resource_name',
            render: (text, record) => <div>{record.resource_name}</div>,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            minWidth: 100,
            render: (text, record) => (
                <div>
                    <ResourceStatus
                        isEnabled={record.is_enabled}
                        peakHoursReady={!isSeasonalDataMissing}
                        seasonalData={record.seasonal_energy_data ? record.seasonal_energy_data[currentSeasonInfo.id] : null}
                    />
                </div>
            ),
        },
        {
            title: 'Aggregation Id',
            dataIndex: 'aggregation_id',
            minWidth: 100,
            render: (text, record) => <div>{record.aggregation_id}</div>,
        },
        {
            title: 'Enrollment Date',
            dataIndex: 'enrollment_date',
            minWidth: 120,
            render: (text, record) => {
                if (!record.enrollment_date) {
                    return <></>;
                }
                return <div>{getComputerFormattedTime(text, DEFAULT_DATE_FORMAT)}</div>;
            },
        },
        {
            title: (
                <>
                    <span>Enabled</span>{' '}
                    <Tooltip title="Only Enabled resources will be included in the Enrollment File">
                        <QuestionCircleOutlined style={{ color: SECONDARY_COLOR }} />
                    </Tooltip>
                </>
            ),
            dataIndex: 'is_enabled',
            minWidth: 100,
            render: (text, record) => (
                <div>
                    <ResourceEnableStateSwitch resource={record} />
                </div>
            ),
        },
        {
            key: 'actions',
            fixed: 'right',
            minWidth: 60,
            sorter: false,
            render: (text: string, record) => <ActionList actions={renderActions(record)} item={record} />,
        },
    ];

    return (
        <>
            <PageHeader
                pageTitle={<span style={{ textTransform: 'capitalize' }}>NYISO {currentSeasonInfo.viewValue} </span>}
                actions={[
                    <Button key="nyiso-enable-all" size="large" onClick={() => toggleAllResources({ enable: true })}>
                        Enable All
                    </Button>,
                    <Button key="nyiso-disable-all" size="large" onClick={() => toggleAllResources({ enable: false })}>
                        Disable All
                    </Button>,
                    <Badge dot offset={[-3, 3]} count={isSeasonalDataMissing ? 1 : 0} title={''} key="nyiso-seasonal-data">
                        <Button size="large" icon={<UploadOutlined />} onClick={onSeasonModalOpen} disabled={isSeasonError || isSeasonLoading}>
                            {isSeasonalDataMissing && (
                                <Popover
                                    content={
                                        <>
                                            <div>Peak hours data for current season is missing!</div>
                                            <div>Upload it using the 'Upload Peak Hours' button.</div>
                                        </>
                                    }
                                >
                                    <WarningTwoTone twoToneColor={WARNING_COLOR} style={{ fontSize: 'large' }} />
                                </Popover>
                            )}
                            Upload Peak Hours
                        </Button>
                    </Badge>,
                    <Button
                        key="nyiso-download"
                        size="large"
                        disabled={!isAllowedToDownloadResources}
                        loading={exportLoading}
                        icon={<DownloadOutlined />}
                        onClick={onEnrollmentFileRequest}
                    >
                        Download Enrollment File
                    </Button>,
                    <Button
                        key="nyiso-add-resource"
                        size="large"
                        type="primary"
                        icon={<PlusCircleOutlined />}
                        onClick={() => onResourceFormModalOpen()}
                    >
                        Add Resource
                    </Button>,
                ]}
            />

            <WithEmptyDataTable condition={isLoading || isResourcesToggleLoading}>
                <Table
                    size="small"
                    rowKey="resource_id"
                    className="nyiso-list-table"
                    rowClassName={record => (record.is_enabled ? '' : 'nyiso-disabled-row')}
                    columns={columns}
                    dataSource={resources?.data}
                    loading={isLoading || isResourcesToggleLoading || isResourceToReprocessLoading}
                    onChange={onTableChange}
                    pagination={getTablePaginationParams(resources?.meta)}
                    sticky={{ offsetHeader: 62 }}
                    tableLayout={'auto'}
                    scroll={{ x: 'scroll' }}
                />
            </WithEmptyDataTable>
            {resource && pageAction === PAGE_ACTION.NEW_EDIT && (
                <ResourceFormModal resource={resource} onClose={onResourceModalClose} isEdit={!!resource.resource_id} />
            )}
            {pageAction === PAGE_ACTION.UPLOAD_FILE && (
                <PeakHoursUploadModal peakHoursData={seasonData?.peak_hours} currentSeasonInfo={currentSeasonInfo} onClose={onSeasonModalClose} />
            )}
        </>
    );
};
